import type { CSSProperties } from "react"
import { ArrowRight, Radio } from "lucide-react"
import { Button } from "@workspace/ui/components/button"
import { useI18n } from "@/lib/i18n"

const delay = (n: number) => ({ "--d": n } as CSSProperties)

type WaveGroup = {
  count: number
  y: number
  spacing: number
  dir: 1 | -1
  baseAlpha: number
  alphaDivisor: number
  stroke: "a" | "b"
  strokeWidth?: number
  anim: "a" | "b" | "c" | "d"
  dur: number
  delay?: number
  reverse?: boolean
  ease?: string
  origin?: string
  shape: (y: number) => string
}

// Distribution: sparse top, sparser middle, dense bottom.
const WAVE_GROUPS: WaveGroup[] = [
  // ── TOP: a few sparse strands ───────────────────────────────
  {
    count: 5, y: 90, spacing: 5, dir: 1,
    baseAlpha: 0.14, alphaDivisor: 22,
    stroke: "b", anim: "c", dur: 20, delay: 0.4, ease: "linear",
    shape: (y) =>
      `M-260,${y + 20} C120,${y - 50} 340,${y + 60} 600,${y} C820,${y - 50} 1020,${y + 50} 1260,${y - 10} C1440,${y - 40} 1580,${y + 30} 1700,${y + 10}`,
  },
  {
    count: 3, y: 170, spacing: 4, dir: 1,
    baseAlpha: 0.2, alphaDivisor: 10,
    stroke: "a", strokeWidth: 1.1,
    anim: "a", dur: 16, delay: 1.2, ease: "linear",
    shape: (y) =>
      `M500,${y} C720,${y - 40} 940,${y + 50} 1180,${y - 10} C1380,${y - 50} 1540,${y + 30} 1700,${y}`,
  },

  // ── MIDDLE: very few, thin, scattered ───────────────────────
  {
    count: 2, y: 340, spacing: 14, dir: 1,
    baseAlpha: 0.22, alphaDivisor: 4,
    stroke: "b", strokeWidth: 1,
    anim: "c", dur: 24, delay: 0.8, ease: "linear",
    shape: (y) =>
      `M-260,${y} C160,${y - 40} 380,${y + 50} 640,${y - 10} C900,${y - 50} 1120,${y + 40} 1360,${y} C1540,${y - 30} 1620,${y + 20} 1700,${y + 10}`,
  },
  {
    count: 2, y: 440, spacing: 12, dir: 1,
    baseAlpha: 0.2, alphaDivisor: 4,
    stroke: "a", strokeWidth: 1.1,
    anim: "d", dur: 22, delay: 0.2, reverse: true, ease: "linear",
    shape: (y) =>
      `M-260,${y + 10} C120,${y + 60} 380,${y - 60} 640,${y + 10} C900,${y + 70} 1120,${y - 50} 1360,${y - 10} C1520,${y + 30} 1620,${y - 20} 1700,${y}`,
  },

  // ── LOWER-MIDDLE: starting to thicken ───────────────────────
  {
    count: 8, y: 560, spacing: 5, dir: 1,
    baseAlpha: 0.12, alphaDivisor: 30,
    stroke: "a", anim: "a", dur: 22, delay: 0, ease: "linear",
    shape: (y) =>
      `M-260,${y} C60,${y - 80} 260,${y + 100} 520,${y} C780,${y - 70} 920,${y + 60} 1200,${y - 20} C1380,${y - 50} 1540,${y + 40} 1700,${y - 20}`,
  },
  {
    count: 6, y: 600, spacing: 3, dir: -1,
    baseAlpha: 0.16, alphaDivisor: 20,
    stroke: "a", strokeWidth: 1.2,
    anim: "b", dur: 14, delay: 0.6, reverse: true, ease: "linear",
    shape: (y) =>
      `M-260,${y} C80,${y + 70} 260,${y - 60} 520,${y + 40} C780,${y + 90} 920,${y - 40} 1200,${y + 60} C1380,${y + 80} 1540,${y - 20} 1700,${y + 30}`,
  },

  // ── BOTTOM: the densest clusters ────────────────────────────
  {
    count: 22, y: 640, spacing: 3, dir: 1,
    baseAlpha: 0.1, alphaDivisor: 55,
    stroke: "b", anim: "c", dur: 18, delay: 0.2, ease: "linear",
    shape: (y) =>
      `M-260,${y + 30} C140,${y - 40} 320,${y + 100} 600,${y - 20} C820,${y - 80} 960,${y + 60} 1180,${y - 10} C1400,${y - 70} 1560,${y + 40} 1700,${y}`,
  },
  {
    count: 18, y: 720, spacing: 3, dir: 1,
    baseAlpha: 0.12, alphaDivisor: 50,
    stroke: "a", anim: "b", dur: 21, delay: 0.7, ease: "linear",
    shape: (y) =>
      `M-260,${y - 10} C120,${y - 80} 360,${y + 50} 620,${y - 20} C880,${y - 70} 1080,${y + 40} 1320,${y - 30} C1500,${y - 70} 1620,${y + 20} 1700,${y - 10}`,
  },
  {
    count: 12, y: 780, spacing: 4, dir: 1,
    baseAlpha: 0.14, alphaDivisor: 30,
    stroke: "a", strokeWidth: 1.1,
    anim: "d", dur: 16, delay: 1.1, ease: "linear",
    shape: (y) =>
      `M-260,${y + 20} C180,${y - 40} 420,${y + 70} 680,${y} C920,${y - 60} 1120,${y + 50} 1360,${y - 10} C1520,${y - 40} 1620,${y + 20} 1700,${y + 5}`,
  },
  {
    count: 9, y: 830, spacing: 3, dir: 1,
    baseAlpha: 0.18, alphaDivisor: 22,
    stroke: "b", anim: "a", dur: 24, delay: 0.3, reverse: true, ease: "linear",
    shape: (y) =>
      `M-260,${y + 10} C160,${y - 30} 400,${y + 60} 680,${y - 10} C940,${y - 50} 1160,${y + 40} 1400,${y - 20} C1560,${y - 40} 1640,${y + 20} 1700,${y}`,
  },
]

export function Hero() {
  const { t } = useI18n()

  return (
    <section className="relative flex flex-1 flex-col justify-center overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32">
      {/* ─── WAVE BACKGROUND ──────────────────────────────────────── */}

      {/* Base — soft blue gradient wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #F5F8FF 0%, #E6ECFD 35%, #D4DFFB 70%, #B6C6F2 100%)",
        }}
      />

      {/* Flowing wave layers — stroke only, dense line-art */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="wave-stroke" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3F51B5" stopOpacity="0" />
            <stop offset="12%" stopColor="#3F51B5" stopOpacity="0.85" />
            <stop offset="50%" stopColor="#5A6FD8" stopOpacity="1" />
            <stop offset="88%" stopColor="#3F51B5" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#3F51B5" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="wave-stroke-2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6B85F2" stopOpacity="0" />
            <stop offset="50%" stopColor="#6B85F2" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#6B85F2" stopOpacity="0" />
          </linearGradient>
        </defs>

        {WAVE_GROUPS.map((g, gi) => {
          const stroke = g.stroke === "a" ? "url(#wave-stroke)" : "url(#wave-stroke-2)"
          const scatters = ["a", "b", "c"] as const
          return (
            <g
              key={`wg-${gi}`}
              fill="none"
              stroke={stroke}
              strokeWidth={g.strokeWidth ?? 1}
              vectorEffect="non-scaling-stroke"
              style={{
                animation: `wave-drift-${g.anim} ${g.dur}s ${g.ease ?? "ease-in-out"} ${g.delay ?? 0}s infinite ${g.reverse ? "reverse" : ""}`,
                transformOrigin: g.origin ?? "50% 50%",
              }}
            >
              {Array.from({ length: g.count }).map((_, i) => {
                const y = g.y + i * g.spacing * g.dir
                const a = g.baseAlpha + Math.min(i, g.count - i) / g.alphaDivisor
                const s = scatters[(gi * 7 + i) % scatters.length]
                const sDur = 6 + ((gi * 3 + i * 2) % 7)
                const sDelay = ((gi * 5 + i * 3) % 11) * 0.35
                return (
                  <path
                    key={`wg-${gi}-${i}`}
                    opacity={Math.min(a, 0.95)}
                    d={g.shape(y)}
                    style={{
                      animation: `wave-scatter-${s} ${sDur}s ease-in-out ${sDelay}s infinite`,
                      transformOrigin: "50% 50%",
                    }}
                  />
                )
              })}
            </g>
          )
        })}
      </svg>

      {/* Full-bleed glass pane — frosts the waves behind the text */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgb(255 255 255 / 0.7) 0%, rgb(245 248 255 / 0.6) 45%, rgb(220 230 255 / 0.4) 75%, rgb(140 165 240 / 0.3) 92%, rgb(63 81 181 / 0.3) 100%)",
          backdropFilter: "blur(3px) saturate(140%)",
          WebkitBackdropFilter: "blur(3px) saturate(140%)",
          animation: "glass-shimmer 10s ease-in-out infinite",
        }}
      />

      {/* Glass highlight hairline — top sheen */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgb(255 255 255 / 0.75), transparent)",
        }}
      />

      {/* ─── CONTENT ────────────────────────────────────────────────── */}
      <div className="relative mx-auto w-full max-w-[1400px] px-6 md:px-14 xl:px-20">
        {/* Eyebrow */}
        <div
          className="rise flex items-center gap-2 font-mono text-[12px] tracking-[0.02em] text-foreground/60"
          style={delay(0)}
        >
          <span className="relative inline-flex size-2">
            <span
              aria-hidden
              className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60"
            />
            <span className="relative inline-flex size-2 rounded-full bg-primary" />
          </span>
          {t.hero.eyebrow}
        </div>

        {/* Headline */}
        <h1 className="rise-stagger mt-7 max-w-[20ch] text-[clamp(2rem,5.2vw,4.6rem)] leading-[1.04] font-extrabold tracking-[-0.03em]">
          <span style={delay(1)} className="block text-primary">
            Blumen Solutions.
          </span>
          <span style={delay(2)} className="mt-1 block text-foreground">
            {t.hero.tagline} {t.hero.taglineEm}
          </span>
        </h1>

        {/* Lead */}
        <p
          className="rise mt-8 max-w-xl text-[15.5px] leading-[1.65] text-foreground/75 md:text-[17px]"
          style={delay(3)}
        >
          {t.hero.lead}
        </p>

        {/* Status */}
        <div
          className="rise mt-8 flex flex-wrap items-center gap-4"
          style={delay(4)}
        >
          <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/80">
            <Radio
              className="size-3.5 text-primary"
              strokeWidth={1.8}
              aria-hidden
            />
            {t.hero.metaValue}
          </span>
        </div>

        {/* CTAs */}
        <div
          className="rise mt-7 flex flex-wrap items-center gap-3"
          style={delay(5)}
        >
          <Button
            asChild
            size="xl"
            className="rounded-none bg-primary font-mono text-[12px] uppercase tracking-[0.18em] text-primary-foreground shadow-sm hover:bg-primary/90"
          >
            <a href="/#work">
              {t.hero.ctaPrimary}
              <ArrowRight />
            </a>
          </Button>
          <Button
            asChild
            variant="ghost"
            size="xl"
            className="rounded-none px-4 font-mono text-[12px] uppercase tracking-[0.18em] text-foreground/75 underline-offset-4 hover:bg-foreground/5 hover:text-foreground hover:underline"
          >
            <a href="/#contact">{t.hero.ctaSecondary}</a>
          </Button>
        </div>
      </div>
    </section>
  )
}
